/* ============================================================
   Startup Rev — Cart drawer
   Defines <cart-drawer> custom element so Dawn product-form.js
   can call open / setActiveElement / renderContents on it.
   Self-contained: handles +/- quantity, remove, optimistic
   updates, and live nav-badge sync. Reuses no Dawn cart code.
   ============================================================ */

(() => {
  if (customElements.get('cart-drawer')) return;

  const SECTION_ID = 'cart-drawer';
  const getAjaxUrl = (routeName, fallback) => {
    const route = (window.routes && window.routes[routeName]) || fallback;
    return `${route}.js`;
  };
  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'textarea:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  class SrCartDrawer extends HTMLElement {
    constructor() {
      super();
      this._activeElement = null;
      this._onKeyDown = this._onKeyDown.bind(this);
      this._onClick = this._onClick.bind(this);
      this._onChange = this._onChange.bind(this);
      this._onSubmit = this._onSubmit.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this._onClick);
      this.addEventListener('change', this._onChange);
      this.addEventListener('submit', this._onSubmit);

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        const input = event.target.closest('[data-sr-drawer-qty-input]');
        if (!input) return;
        event.preventDefault();
        input.blur();
      });

      // Public API for other scripts.
      window.SRCartDrawer = window.SRCartDrawer || {};
      window.SRCartDrawer.open = () => this.open();
      window.SRCartDrawer.close = () => this.close();
      window.SRCartDrawer.refresh = () => this._refresh();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKeyDown);
      document.body.classList.remove('sr-drawer-open');
    }

    /* ---------- Open / close ---------- */

    open(triggerEl) {
      if (triggerEl) this.setActiveElement(triggerEl);

      // Force a reflow so the transition runs even when activated immediately
      // after a re-render (when the panel was just inserted into the DOM).
      this.classList.add('active');
      this.offsetHeight; // force reflow
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('sr-drawer-open');
      document.addEventListener('keydown', this._onKeyDown);

      // Move focus into the drawer for keyboard users.
      requestAnimationFrame(() => {
        const close = this.querySelector('.sr-drawer__close');
        if (close) close.focus({ preventScroll: true });
      });
    }

    close() {
      this.classList.remove('active');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('sr-drawer-open');
      document.removeEventListener('keydown', this._onKeyDown);

      const restore = this._activeElement;
      this._activeElement = null;
      if (restore && typeof restore.focus === 'function') {
        try {
          restore.focus({ preventScroll: true });
        } catch (_e) {
          /* noop */
        }
      }
    }

    setActiveElement(element) {
      this._activeElement = element;
    }

    /* ---------- Sections (used by Dawn product-form.js) ---------- */

    getSectionsToRender() {
      return [{ id: SECTION_ID, selector: '#CartDrawer' }];
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      const dom = new DOMParser().parseFromString(html, 'text/html');
      const node = dom.querySelector(selector) || dom.querySelector('#CartDrawer');
      return node ? node.innerHTML : '';
    }

    _replaceDrawerContentsFromHtml(html) {
      const target = this.querySelector('#CartDrawer');
      if (!html || !target) return false;

      const contents = this.getSectionInnerHTML(html, '#CartDrawer');
      if (!contents) return false;
      target.innerHTML = contents;
      target.querySelectorAll('.sr-ticket').forEach((el) => el.classList.add('no-anim'));
      return true;
    }

    /**
     * Called by product-form.js after a successful add.
     * `parsedState` is the JSON response from /cart/add.js with a
     * `sections` map keyed by section id. `/cart/add.js` does NOT
     * include `item_count`, so we sync the nav badge from /cart.js
     * after re-rendering.
     */
    renderContents(parsedState) {
      if (!parsedState) return;
      const html = parsedState.sections && parsedState.sections[SECTION_ID];
      if (this._replaceDrawerContentsFromHtml(html)) {
        const target = this.querySelector('#CartDrawer');
        const tickets = target.querySelectorAll('.sr-ticket');
        tickets.forEach((el, idx) => {
          if (idx < tickets.length - 1) el.classList.add('no-anim');
        });
      }

      this._syncNavBadgeFromCartJs();

      setTimeout(() => this.open(), 0);
    }

    /* ---------- Internal: refresh from /cart ---------- */

    async _refresh() {
      try {
        const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
        const response = await fetch(`${cartUrl}?sections=${SECTION_ID}`);
        if (!response.ok) return false;
        const data = await response.json();
        const html = data && data[SECTION_ID];
        return this._replaceDrawerContentsFromHtml(html);
      } catch (_e) {
        return false;
      }
    }

    async _getCart() {
      const response = await fetch(getAjaxUrl('cart_url', '/cart'), {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Could not load cart.');
      return response.json();
    }

    async _syncNavBadgeFromCartJs() {
      try {
        const cart = await this._getCart();
        this._syncNavBadge(cart);
      } catch (_e) {
        /* noop */
      }
    }

    _syncNavBadge(cartLike) {
      if (!cartLike || typeof cartLike.item_count === 'undefined') return;
      const count = Number(cartLike.item_count) || 0;
      const label = count > 0 ? 'Current Passes' : 'Get Your Pass';

      document.querySelectorAll('[data-sr-cart-label]').forEach((node) => {
        node.textContent = label;
      });

      document.querySelectorAll('[data-sr-cart-count]').forEach((node) => {
        node.textContent = String(count);
        node.classList.toggle('is-empty', count === 0);
        node.setAttribute('aria-label', `${count} items in cart`);
      });
    }

    /* ---------- Internal: events ---------- */

    _onKeyDown(event) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusables = Array.from(this.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null
      );
      if (!focusables.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    _onClick(event) {
      const overlay = event.target.closest('[data-sr-drawer-overlay]');
      if (overlay) {
        event.preventDefault();
        this.close();
        return;
      }

      const close = event.target.closest('[data-sr-drawer-close]');
      if (close) {
        event.preventDefault();
        this.close();
        return;
      }

      const closeLink = event.target.closest('[data-sr-drawer-close-link]');
      if (closeLink) {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;

        event.preventDefault();
        const href = closeLink.href;
        this.close();
        if (href) window.location.assign(href);
        return;
      }

      const remove = event.target.closest('[data-sr-drawer-remove]');
      if (remove) {
        event.preventDefault();
        const line = remove.dataset.line;
        this._updateLine(line, 0, { animateRemove: true });
        return;
      }

      const stepper = event.target.closest('[data-sr-drawer-qty]');
      if (stepper) {
        event.preventDefault();
        const line = stepper.dataset.line;
        const input = this.querySelector(`#DrawerQuantity-${line}`);
        if (!input) return;
        const direction = stepper.dataset.srDrawerQty === 'plus' ? 1 : -1;
        const next = Math.max(0, parseInt(input.value, 10) + direction);
        input.value = String(next);
        this._updateLine(line, next, { animateRemove: next === 0 });
      }
    }

    _onChange(event) {
      const input = event.target.closest('[data-sr-drawer-qty-input]');
      if (!input) return;
      const line = input.dataset.line;
      const next = Math.max(0, parseInt(input.value, 10) || 0);
      input.value = String(next);
      this._updateLine(line, next, { animateRemove: next === 0 });
    }

    _onSubmit(event) {
      const form = event.target.closest('[data-sr-bogo-form]');
      if (!form) return;
      event.preventDefault();
      this._claimBogo(form);
    }

    _setLineUpdating(line, on) {
      const card = this.querySelector(`[data-sr-drawer-line="${line}"]`);
      if (card) card.classList.toggle('is-updating', !!on);
    }

    _showError(line, message) {
      const errorEl = this.querySelector(`#DrawerLineError-${line}`);
      if (errorEl) {
        const text = errorEl.querySelector('.cart-item__error-text');
        if (text) text.textContent = message || '';
      }
      const cartErrors = this.querySelector('.sr-drawer__errors');
      if (cartErrors) cartErrors.textContent = message || '';
    }

    _announce(message) {
      const region = this.querySelector('#sr-drawer-live-region');
      if (!region) return;
      region.textContent = message || '';
    }

    _setBogoUpdating(form, on) {
      const submit = form.querySelector('[data-sr-bogo-submit]');
      const label = form.querySelector('[data-sr-bogo-submit-label]');
      form.setAttribute('aria-busy', on ? 'true' : 'false');
      if (submit) submit.disabled = !!on;
      if (label) label.textContent = on ? 'Adding ticket...' : 'Add my free ticket';
    }

    _showBogoStatus(form, message) {
      const status = form.querySelector('[data-sr-bogo-status]');
      if (status) status.textContent = message || '';
    }

    async _claimBogo(form) {
      const variant = form.querySelector('[name="id"]');
      if (!variant || form.getAttribute('aria-busy') === 'true') return;

      this.classList.add('is-loading');
      this._setBogoUpdating(form, true);
      this._showBogoStatus(form, '');

      try {
        const response = await fetch(getAjaxUrl('cart_add_url', '/cart/add'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(variant.value), quantity: 1 }],
            sections: [SECTION_ID],
            sections_url: window.location.pathname,
          }),
        });
        const result = await response.json();

        if (!response.ok) {
          const message = (result && (result.description || result.message)) || 'Could not add the free ticket.';
          this._showBogoStatus(form, message);
          this._announce(message);
          this._setBogoUpdating(form, false);
          return;
        }

        let rendered = this._replaceDrawerContentsFromHtml(result.sections && result.sections[SECTION_ID]);
        if (!rendered) rendered = await this._refresh();
        if (!rendered) {
          window.location.assign((window.routes && window.routes.cart_url) || '/cart');
          return;
        }

        const cart = await this._getCart();
        this._announce('Free ticket added. Your offer has been applied.');
        this._syncNavBadge(cart);
        document.dispatchEvent(new CustomEvent('sr:cart-updated', { detail: { cart } }));
      } catch (_e) {
        const message = 'Network error. Please try again.';
        this._showBogoStatus(form, message);
        this._announce(message);
        this._setBogoUpdating(form, false);
      } finally {
        this.classList.remove('is-loading');
      }
    }

    async _updateLine(line, quantity, { animateRemove = false } = {}) {
      this.classList.add('is-loading');
      this._setLineUpdating(line, true);
      this._showError(line, '');

      const card = this.querySelector(`[data-sr-drawer-line="${line}"]`);

      try {
        const response = await fetch(getAjaxUrl('cart_change_url', '/cart/change'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            line: Number(line),
            quantity: Number(quantity),
            sections: [SECTION_ID],
            sections_url: window.location.pathname,
          }),
        });
        const cart = await response.json();

        if (!response.ok) {
          const message = (cart && (cart.description || cart.message)) || 'Could not update cart.';
          this._showError(line, message);
          this._announce(message);
          this._setLineUpdating(line, false);
          this.classList.remove('is-loading');
          return;
        }

        this._announce(quantity === 0 ? 'Pass removed.' : 'Quantity updated.');
        this._syncNavBadge(cart);
        document.dispatchEvent(new CustomEvent('sr:cart-updated', { detail: { cart } }));

        if (animateRemove && quantity === 0 && card) {
          card.classList.add('is-removing');
          await new Promise((resolve) => setTimeout(resolve, 280));
        }

        const rendered = this._replaceDrawerContentsFromHtml(cart.sections && cart.sections[SECTION_ID]);
        if (!rendered) await this._refresh();
      } catch (_e) {
        this._showError(line, 'Network error. Please try again.');
      } finally {
        this.classList.remove('is-loading');
      }
    }
  }

  customElements.define('cart-drawer', SrCartDrawer);

  /* ============================================================
     Hook nav cart triggers — open the drawer instead of navigating.
     Cmd/ctrl/middle-click still go to /cart for power users.
     ============================================================ */

  const onTriggerClick = (event) => {
    const trigger = event.target.closest('[data-sr-cart-open], [data-sr-pass-cart]');
    if (!trigger) return;

    // Skip if cart is empty — the link still points to a meaningful place
    // (e.g. /pages/passes) and there's nothing to preview.
    const countEl = document.querySelector('[data-sr-cart-count]');
    if (countEl && countEl.classList.contains('is-empty')) return;

    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;

    event.preventDefault();
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) {
      window.location.assign((window.routes && window.routes.cart_url) || '/cart');
      return;
    }
    drawer.setActiveElement(trigger);
    drawer.open();
  };

  document.addEventListener('click', onTriggerClick);
})();
